import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api";
import LoadingSpinner from "../components/LoadingSpinner";
import ClientBackground from "../components/ClientBackground";

// Trimester color configurations
const TRIMESTER_COLORS = {
  1: {
    gradient: "from-rose-400 via-pink-400 to-fuchsia-500",
    bg: "bg-rose-50",
    border: "border-rose-300",
    node: "bg-gradient-to-br from-rose-400 to-pink-500",
    nodeRing: "ring-rose-200",
    text: "text-rose-600",
    light: "bg-rose-100",
    stroke: "#f472b6", // A nice pink
    fill: "#ffe4e6",
  },
  2: {
    gradient: "from-violet-400 via-purple-400 to-indigo-500",
    bg: "bg-violet-50",
    border: "border-violet-300",
    node: "bg-gradient-to-br from-violet-400 to-purple-500",
    nodeRing: "ring-violet-200",
    text: "text-violet-600",
    light: "bg-violet-100",
    stroke: "#8b5cf6", // A nice purple
    fill: "#f5f3ff",
  },
  3: {
    gradient: "from-sky-400 via-cyan-400 to-teal-500",
    bg: "bg-sky-50",
    border: "border-sky-300",
    node: "bg-gradient-to-br from-sky-400 to-cyan-500",
    nodeRing: "ring-sky-200",
    text: "text-sky-600",
    light: "bg-sky-100",
    stroke: "#38bdf8", // A nice blue
    fill: "#e0f2fe",
  },
};

const getTrimester = (weekNumber) => {
  if (weekNumber <= 12) return 1;
  if (weekNumber <= 26) return 2;
  return 3;
};

const getTrimesterName = (trimester) => {
  const names = {
    1: "First Trimester",
    2: "Second Trimester",
    3: "Third Trimester",
  };
  return names[trimester] || "";
};

export default function PregnancyTimeline() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [child, setChild] = useState(null);
  const [weeksData, setWeeksData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [updatingTask, setUpdatingTask] = useState(null);
  const timelineRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const accessToken = localStorage.getItem("access");
        const headers = { Authorization: `Bearer ${accessToken}` };

        // Fetch child profile
        const childRes = await api.get(`/children/profiles/${childId}/`, { headers });
        setChild(childRes.data);

        // Fetch pregnancy weeks with tasks
        const weeksRes = await api.get(
          `/children/child-task-completions/child-all-weeks/?child_id=${childId}`,
          { headers }
        );
        setWeeksData(weeksRes.data);
      } catch (err) {
        setError(err.message || "Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [childId]);

  const handleToggleTask = async (taskId) => {
    const originalWeeksData = JSON.parse(JSON.stringify(weeksData));
    const originalSelectedWeek = selectedWeek ? JSON.parse(JSON.stringify(selectedWeek)) : null;

    // Optimistically update the UI
    const updateTasks = (tasks) =>
      tasks.map((task) =>
        task.id === taskId ? { ...task, is_complete: !task.is_complete } : task
      );

    setWeeksData((prev) =>
      prev.map((weekData) => ({
        ...weekData,
        tasks: updateTasks(weekData.tasks),
      }))
    );

    if (selectedWeek) {
      setSelectedWeek((prev) => ({
        ...prev,
        tasks: updateTasks(prev.tasks),
      }));
    }

    setUpdatingTask(taskId);

    try {
      const accessToken = localStorage.getItem("access");
      await api.post(
        "/children/child-task-completions/toggle-task/",
        { child_id: childId, task_id: taskId },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
    } catch (err) {
      console.error("Failed to toggle task:", err);
      // Revert on error
      setWeeksData(originalWeeksData);
      if (originalSelectedWeek) {
        setSelectedWeek(originalSelectedWeek);
      }
      // You could add a toast here to inform the user of the failure
    } finally {
      setUpdatingTask(null);
    }
  };

  const calculateCurrentWeek = () => {
    if (!child?.dob) return 1;
    const dueDate = new Date(child.dob);
    const today = new Date();
    const diffTime = dueDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const weeksRemaining = Math.ceil(diffDays / 7);
    const currentWeek = Math.max(1, Math.min(40, 40 - weeksRemaining));
    return currentWeek;
  };

  const calculateProgress = () => {
    const totalTasks = weeksData.reduce((sum, w) => sum + (w.tasks?.length || 0), 0);
    const completedTasks = weeksData.reduce(
      (sum, w) => sum + (w.tasks?.filter((t) => t.is_complete).length || 0),
      0
    );
    return totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  };

  const currentWeek = calculateCurrentWeek();
  const progress = calculateProgress();

  if (loading) {
    return <LoadingSpinner fullScreen={true} text="Loading your pregnancy journey..." />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center p-8">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-[#1e3a5f] text-white rounded-lg"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-rose-50 to-teal-50">
      <ClientBackground />

      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="fixed top-4 left-4 z-50 p-2 bg-white/80 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-all"
      >
        <svg className="h-6 w-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-8 max-w-4xl mx-auto">
        {/* Top Summary Card */}
        <TopCard child={child} currentWeek={currentWeek} progress={progress} weeksData={weeksData} />

        {/* Timeline */}
        <div ref={timelineRef} className="mt-8 pb-20">
          <ZigZagTimeline
            weeksData={weeksData}
            currentWeek={currentWeek}
            onSelectWeek={setSelectedWeek}
          />
        </div>
      </div>

      {/* Week Detail Modal */}
      {selectedWeek && (
        <WeekDetailModal
          weekData={selectedWeek}
          currentWeek={currentWeek}
          onClose={() => setSelectedWeek(null)}
          onToggleTask={handleToggleTask}
          updatingTask={updatingTask}
        />
      )}
    </div>
  );
}

// Top Summary Card Component
const TopCard = ({ child, currentWeek, progress, weeksData }) => {
  const trimester = getTrimester(currentWeek);
  const colors = TRIMESTER_COLORS[trimester];
  const currentWeekData = weeksData.find((w) => w.week_number === currentWeek);

  const getDaysUntilDue = () => {
    if (!child?.dob) return null;
    const dueDate = new Date(child.dob);
    const today = new Date();
    const diffTime = dueDate - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const daysUntilDue = getDaysUntilDue();

  return (
    <div className={`relative overflow-hidden rounded-3xl bg-gradient-to-br ${colors.gradient} p-6 shadow-2xl`}>
      {/* Decorative circles */}
      <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full" />
      <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-white/10 rounded-full" />

      <div className="relative z-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-white/80 text-sm font-medium">Your Journey</p>
            <h1 className="text-white text-2xl font-bold">
              {child?.first_name}'s Timeline
            </h1>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-2xl px-4 py-2">
            <p className="text-white/80 text-xs">Current</p>
            <p className="text-white text-xl font-bold">Week {currentWeek}</p>
          </div>
        </div>

        {/* Trimester Badge */}
        <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-4">
          <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
          <span className="text-white text-sm font-medium">{getTrimesterName(trimester)}</span>
        </div>

        {/* Baby Icon & Description */}
        <div className="flex items-start gap-4 mb-4">
          <div className="flex-shrink-0 w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
            <BabyIcon className="w-10 h-10 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-white/90 text-sm leading-relaxed">
              {currentWeekData?.week?.description || "Your baby is growing beautifully. Check your weekly tasks to stay on track!"}
            </p>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center">
            <p className="text-white/70 text-xs">Due In</p>
            <p className="text-white text-lg font-bold">
              {daysUntilDue > 0 ? `${daysUntilDue}d` : "Soon!"}
            </p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center">
            <p className="text-white/70 text-xs">Progress</p>
            <p className="text-white text-lg font-bold">{progress}%</p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center">
            <p className="text-white/70 text-xs">Weeks Left</p>
            <p className="text-white text-lg font-bold">{Math.max(0, 40 - currentWeek)}</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-4">
          <div className="h-2 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all duration-500"
              style={{ width: `${(currentWeek / 40) * 100}%` }}
            />
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-white/60 text-xs">Week 1</span>
            <span className="text-white/60 text-xs">Week 40</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ZigZag Timeline Component
const ZigZagTimeline = ({ weeksData, currentWeek, onSelectWeek }) => {
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Responsive settings
  const getTimelineSettings = (width) => {
    if (width < 640) { // Small mobile
      return { nodesPerRow: 3, nodeSpacing: 80, rowHeight: 180, containerWidth: 300, nodeRadius: 20, currentWeekNodeRadius: 24 };
    }
    if (width < 768) { // Mobile
      return { nodesPerRow: 3, nodeSpacing: 100, rowHeight: 190, containerWidth: 380, nodeRadius: 22, currentWeekNodeRadius: 26 };
    }
    if (width < 1024) { // Tablet
      return { nodesPerRow: 4, nodeSpacing: 120, rowHeight: 200, containerWidth: 560, nodeRadius: 28, currentWeekNodeRadius: 32 };
    }
    // Desktop
    return { nodesPerRow: 5, nodeSpacing: 140, rowHeight: 210, containerWidth: 780, nodeRadius: 32, currentWeekNodeRadius: 36 };
  };

  const { nodesPerRow, nodeSpacing, rowHeight, containerWidth, nodeRadius, currentWeekNodeRadius } = getTimelineSettings(width);


  // Sort weeks by week number
  const sortedWeeks = [...weeksData].sort((a, b) => 
    (a.week_number || 0) - (b.week_number || 0)
  );

  // Calculate positions for each week node
  const getNodePosition = (index) => {
    const row = Math.floor(index / nodesPerRow);
    const posInRow = index % nodesPerRow;
    const isEvenRow = row % 2 === 0;

    const x = isEvenRow
      ? 40 + posInRow * nodeSpacing
      : containerWidth - 40 - posInRow * nodeSpacing;

    const y = 40 + row * rowHeight;

    return { x, y, row, posInRow, isEvenRow };
  };

  // Generate SVG path for the snake timeline
  const generatePath = () => {
    if (sortedWeeks.length === 0) return "";

    const positions = sortedWeeks.map((_, i) => getNodePosition(i));
    let path = `M ${positions[0].x} ${positions[0].y}`;

    for (let i = 0; i < positions.length - 1; i++) {
      const current = positions[i];
      const next = positions[i + 1];

      if (current.row === next.row) {
        path += ` L ${next.x} ${next.y}`;
      } else {
        // Create a smooth S-curve using a cubic Bézier
        const cp1x = current.x;
        const cp1y = current.y + rowHeight * 0.6;
        const cp2x = next.x;
        const cp2y = next.y - rowHeight * 0.6;
        path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${next.x} ${next.y}`;
      }
    }
    return path;
  };

  const totalRows = Math.ceil(sortedWeeks.length / nodesPerRow);
  const svgHeight = totalRows * rowHeight + 80;

  // Generate gradient stops for trimester colors
  const generateGradientStops = () => {
    const stops = [];
    const total = sortedWeeks.length;

    sortedWeeks.forEach((weekData, index) => {
      const weekNum = weekData.week_number || 1;
      const trimester = getTrimester(weekNum);
      const colors = TRIMESTER_COLORS[trimester];
      const offset = (index / Math.max(1, total - 1)) * 100;
      stops.push({ offset: `${offset}%`, color: colors.stroke });
    });

    return stops;
  };

  return (
    <div className="relative">
      {/* Trimester Labels */}
      {[1, 2, 3].map((trimester) => {
        const trimesterWeeks = sortedWeeks.filter(
          (w) => getTrimester(w.week_number || 1) === trimester
        );
        if (trimesterWeeks.length === 0) return null;

        const firstWeekIndex = sortedWeeks.findIndex(
          (w) => w.week_number === trimesterWeeks[0]?.week_number
        );
        const pos = getNodePosition(firstWeekIndex);
        const colors = TRIMESTER_COLORS[trimester];

        return (
          <div
            key={trimester}
            className={`absolute left-0 right-0 ${colors.text} text-xs font-semibold opacity-60`}
            style={{ top: pos.y - 30 }}
          >
            <div className={`${colors.light} inline-block px-3 py-1 rounded-full`}>
              {getTrimesterName(trimester)}
            </div>
          </div>
        );
      })}

      <svg
        width={containerWidth}
        height={svgHeight}
        className="mx-auto"
        style={{ overflow: "visible" }}
      >
        <defs>
          <linearGradient id="timelineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            {generateGradientStops().map((stop, i) => (
              <stop key={i} offset={stop.offset} stopColor={stop.color} />
            ))}
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur">
              <animate attributeName="stdDeviation" values="3; 5; 3" dur="2.5s" repeatCount="indefinite" />
            </feGaussianBlur>
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Background path (thicker, lighter) */}
        <path
          d={generatePath()}
          fill="none"
          stroke="url(#timelineGradient)"
          strokeWidth="12"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.2"
        />

        {/* Main path */}
        <path
          d={generatePath()}
          fill="none"
          stroke="url(#timelineGradient)"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#glow)"
        />

        {/* Week Nodes */}
        {sortedWeeks.map((weekData, index) => {
          const pos = getNodePosition(index);
          const weekNum = weekData.week_number || index + 1;
          const trimester = getTrimester(weekNum);
          const colors = TRIMESTER_COLORS[trimester];
          const isCurrent = weekNum === currentWeek;
          const isPast = weekNum < currentWeek;
          const isFuture = !isCurrent && !isPast;
          const completedTasks = weekData.tasks?.filter((t) => t.is_complete).length || 0;
          const totalTasks = weekData.tasks?.length || 0;
          const isComplete = totalTasks > 0 && completedTasks === totalTasks;

          return (
            <g key={weekNum} onClick={() => onSelectWeek(weekData)} style={{ cursor: "pointer" }}>
              {/* Outer ring for current week */}
              {isCurrent && (
                <circle
                  cx={pos.x}
                  cy={pos.y}
                  r={currentWeekNodeRadius + 4}
                  fill="none"
                  stroke={colors.stroke}
                  strokeWidth="2"
                  opacity="0.5"
                  className="animate-ping"
                />
              )}

              {/* Node background */}
              <circle
                cx={pos.x}
                cy={pos.y}
                r={isCurrent ? currentWeekNodeRadius : nodeRadius}
                fill={isPast || isCurrent ? colors.stroke : "#e5e7eb"}
                opacity={isPast ? 0.7 : 1}
                filter={isCurrent ? "url(#glow)" : ""}
              />

              {/* Inner circle */}
              <circle
                cx={pos.x}
                cy={pos.y}
                r={isCurrent ? currentWeekNodeRadius - 6 : nodeRadius - 6}
                fill="white"
              />

              {/* Week number or checkmark */}
              {isComplete && isPast ? (
                <path
                  d={`M ${pos.x - 6} ${pos.y} L ${pos.x - 2} ${pos.y + 4} L ${pos.x + 6} ${pos.y - 4}`}
                  stroke={colors.stroke}
                  strokeWidth="2.5"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ) : isFuture ? (
                <g transform={`translate(${pos.x - 10}, ${pos.y - 10})`}>
                  <path
                    fill="#9ca3af"
                    d="M10 2a4 4 0 00-4 4v2H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2V10a2 2 0 00-2-2h-1V6a4 4 0 00-4-4zm2 6V6a2 2 0 10-4 0v2h4z"
                  />
                </g>
              ) : (
                <text
                  x={pos.x}
                  y={pos.y + 1}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={isCurrent ? "12" : "10"}
                  fontWeight="bold"
                  fill={isPast || isCurrent ? colors.stroke : "#9ca3af"}
                >
                  {weekNum}
                </text>
              )}
              {/* Description under the node */}
              <foreignObject x={pos.x - 50} y={pos.y + nodeRadius + 8} width="100" height="100">
                <p xmlns="http://www.w3.org/1999/xhtml" className="text-xs text-center text-gray-500 leading-tight">
                  {weekData.description?.substring(0, 50) || ""}
                  {weekData.description && weekData.description.length > 50 && '...'}
                </p>
              </foreignObject>


              {/* Progress indicator dots */}
              {totalTasks > 0 && (
                <g>
                  {[...Array(Math.min(totalTasks, 4))].map((_, i) => {
                    const angle = (i * 90 - 135) * (Math.PI / 180);
                    const dotR = isCurrent ? currentWeekNodeRadius + 6 : nodeRadius + 6;
                    const dotX = pos.x + Math.cos(angle) * dotR;
                    const dotY = pos.y + Math.sin(angle) * dotR;
                    const isTaskComplete = i < completedTasks;

                    return (
                      <circle
                        key={i}
                        cx={dotX}
                        cy={dotY}
                        r="3.5"
                        fill={isTaskComplete ? colors.stroke : "#d1d5db"}
                        stroke="white"
                        strokeWidth="1"
                      />
                    );
                  })}
                </g>
              )}
            </g>
          );
        })}
      </svg>

      {/* Empty state */}
      {sortedWeeks.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <BabyIcon className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p>No pregnancy weeks available yet.</p>
          <p className="text-sm">Check back soon!</p>
        </div>
      )}
    </div>
  );
};

// Week Detail Modal / Bottom Sheet
const WeekDetailModal = ({ weekData, currentWeek, onClose, onToggleTask, updatingTask }) => {
  const weekNum = weekData?.week_number || "N/A";
  const trimester = getTrimester(weekNum);
  const colors = TRIMESTER_COLORS[trimester];
  const tasks = weekData.tasks || [];
  const completedCount = tasks.filter((t) => t.is_complete).length;
  const isFuture = weekNum > currentWeek;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Bottom Sheet */}
      <div className="relative w-full max-w-lg bg-white rounded-t-3xl shadow-2xl animate-slide-up max-h-[85vh] overflow-hidden">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
        </div>

        {/* Header */}
        <div className={`px-6 pb-4 bg-gradient-to-r ${colors.gradient}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm">{getTrimesterName(trimester)}</p>
              <h2 className="text-white text-2xl font-bold">Week {weekNum}</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Progress */}
          <div className="mt-4">
            <div className="flex justify-between text-white/80 text-sm mb-1">
              <span>Tasks Completed</span>
              <span>{completedCount} / {tasks.length}</span>
            </div>
            <div className="h-2 bg-white/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-white rounded-full transition-all duration-300"
                style={{ width: tasks.length > 0 ? `${(completedCount / tasks.length) * 100}%` : "0%" }}
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 pb-8 overflow-y-auto" style={{ maxHeight: "calc(85vh - 180px)" }}>
          {/* Baby Development Section */}
          {weekData.description && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <div className={`p-2 ${colors.light} rounded-lg`}>
                  <BabyIcon className={`w-5 h-5 ${colors.text}`} />
                </div>
                <h3 className="font-semibold text-gray-800">Baby's Development</h3>
              </div>
              <div className={`${colors.bg} rounded-xl p-4 border ${colors.border}`}>
                <p className="text-gray-700 leading-relaxed">{weekData.description}</p>
              </div>
            </div>
          )}

          {/* Mother's Tasks Section */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className={`p-2 ${colors.light} rounded-lg`}>
                <ClipboardIcon className={`w-5 h-5 ${colors.text}`} />
              </div>
              <h3 className="font-semibold text-gray-800">Your Weekly Tasks</h3>
            </div>
            
            {isFuture && (
              <div className={`rounded-xl p-4 text-center border ${colors.border} ${colors.bg} mb-4`}>
                <p className={`text-sm font-semibold ${colors.text}`}>
                  These tasks will unlock in Week {weekNum}.
                </p>
              </div>
            )}

            {tasks.length > 0 ? (
              <div className="space-y-3">
                {tasks.map((task) => (
                  <TaskItem
                    key={task.id}
                    task={task}
                    colors={colors}
                    onToggle={() => onToggleTask(task.id)}
                    isUpdating={updatingTask === task.id}
                    isLocked={isFuture}
                  />
                ))}
              </div>
            ) : (
              <div className={`${colors.bg} rounded-xl p-6 text-center border ${colors.border}`}>
                <p className="text-gray-500">No tasks for this week yet.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

// Task Item Component
const TaskItem = ({ task, colors, onToggle, isUpdating, isLocked }) => {
  return (
    <div
      onClick={!isLocked ? onToggle : undefined}
      className={`flex items-start gap-3 p-4 rounded-xl border-2 transition-all ${
        isLocked
          ? "bg-gray-100 border-gray-200 cursor-not-allowed opacity-60"
          : `cursor-pointer ${
              task.is_complete
                ? `${colors.bg} ${colors.border}`
                : "bg-gray-50 border-gray-200 hover:border-gray-300"
            }`
      }`}
    >
      {/* Checkbox */}
      <div className="flex-shrink-0 pt-0.5">
        <div
          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
            task.is_complete
              ? `${colors.node} border-transparent`
              : "border-gray-300 bg-white"
          }`}
        >
          {isUpdating ? (
            <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : task.is_complete ? (
            <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          ) : null}
        </div>
      </div>

      {/* Task Text */}
      <div className="flex-1">
        <p
          className={`text-sm leading-relaxed ${
            task.is_complete ? "text-gray-600" : "text-gray-800"
          }`}
        >
          {task.statement}
        </p>
        {task.completed_at && (
          <p className="text-xs text-gray-400 mt-1">
            Completed {new Date(task.completed_at).toLocaleDateString()}
          </p>
        )}
      </div>
    </div>
  );
};

// Icons
const BabyIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="12" cy="8" r="4" />
    <path d="M12 12c-4 0-6 2-6 5v2h12v-2c0-3-2-5-6-5z" />
    <path d="M9 6.5c-.5-.5-1.5-.5-2 0M15 6.5c.5-.5 1.5-.5 2 0" strokeLinecap="round" />
  </svg>
);

const ClipboardIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
    <rect x="9" y="3" width="6" height="4" rx="1" />
    <path d="M9 12h6M9 16h6" strokeLinecap="round" />
  </svg>
);
